import { useCallback, useEffect, useRef, useState } from "react";
import { modelsApi, modelsProxyUrl } from "../../../../../API/axiosInstance";

const TOPIC_META_ENDPOINT = "/topics/metadata";
const TOPIC_META_SSE_URL = modelsProxyUrl(TOPIC_META_ENDPOINT);
const PAGE_SIZE = 10;

function buildSortParams(sort) {
  if (sort === "likes")  return { sort_by_likes:  true };
  if (sort === "reach")  return { sort_by_reach:  true };
  if (sort === "newest") return { sort_by_newest: true };
  if (sort === "oldest") return { sort_by_oldest: true };
  if (sort === "media")  return { sort_by_media:  true };
  return {};
}

function buildRestParams(topicId, dataType, filters, page, topicType) {
  const platform = filters.selectedPlatforms?.length
    ? filters.selectedPlatforms.join(",")
    : undefined;
  return {
    topic_id: topicId,
    page,
    page_size: PAGE_SIZE,
    ...(topicType               ? { type: topicType }              : {}),
    ...(platform                ? { platform }                     : {}),
    ...(filters.searchQuery     ? { query: filters.searchQuery }   : {}),
    ...(filters.fromDate        ? { from_date: filters.fromDate }  : {}),
    ...(filters.toDate          ? { to_date: filters.toDate }      : {}),
    ...(dataType !== "all"      ? { data_type: dataType }          : {}),
    ...(filters.personalized    ? { personalized: true }           : {}),
    ...buildSortParams(filters.sortOption),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTopicPosts — REST with infinite scroll
// filtersRef is a MutableRefObject kept fresh by the caller on every render.
// ─────────────────────────────────────────────────────────────────────────────
export function useTopicPosts({ topicId, dataType, filtersRef, enabled, topicType }) {
  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [hasMore,       setHasMore]       = useState(false);
  const [dateRange,     setDateRange]     = useState(null);
  const [platformsInfo, setPlatformsInfo] = useState([]);

  const initAbortRef   = useRef(null);
  const isFetchingMore = useRef(false);

  // Always-fresh primitive refs
  const topicIdRef    = useRef(topicId);
  const dataTypeRef   = useRef(dataType);
  const topicTypeRef  = useRef(topicType);
  const pageRef       = useRef(page);
  const totalPagesRef = useRef(totalPages);
  topicIdRef.current    = topicId;
  dataTypeRef.current   = dataType;
  topicTypeRef.current  = topicType;
  pageRef.current       = page;
  totalPagesRef.current = totalPages;

  const fetchInitial = useCallback(async (tid, type) => {
    initAbortRef.current?.abort();
    const controller = new AbortController();
    initAbortRef.current = controller;

    setLoading(true); setError(null); setPosts([]); setPage(1);

    try {
      const params = buildRestParams(tid, type, filtersRef.current, 1, topicTypeRef.current);
      const { data } = await modelsApi.get(TOPIC_META_ENDPOINT, {
        signal: controller.signal,
        params,
      });
      const tp = data.total_pages ?? 1;
      setPosts(data.data ?? []);
      setTotalPages(tp);
      setTotalCount(data.total_data ?? 0);
      setHasMore(1 < tp);
      setDateRange(data.date_range ?? null);
      setPlatformsInfo(data.platforms_info ?? []);
    } catch (e) {
      if (e.name !== "CanceledError" && e.name !== "AbortError")
        setError("No posts found for this topic");
    } finally {
      setLoading(false);
    }
  }, [filtersRef]);

  const loadMore = useCallback(async () => {
    if (isFetchingMore.current) return;
    const nextPage = pageRef.current + 1;
    if (nextPage > totalPagesRef.current) return;

    isFetchingMore.current = true;
    setLoadingMore(true);

    try {
      const params = buildRestParams(
        topicIdRef.current,
        dataTypeRef.current,
        filtersRef.current,
        nextPage,
        topicTypeRef.current,
      );
      const { data } = await modelsApi.get(TOPIC_META_ENDPOINT, { params });
      const tp = data.total_pages ?? 1;
      setPosts((prev) => [...prev, ...(data.data ?? [])]);
      setTotalPages(tp);
      setTotalCount(data.total_data ?? 0);
      setPage(nextPage);
      setHasMore(nextPage < tp);
    } catch (_) {
      // silent — user can scroll back to retry
    } finally {
      isFetchingMore.current = false;
      setLoadingMore(false);
    }
  }, [filtersRef]);

  useEffect(() => {
    if (!enabled || !topicId) { initAbortRef.current?.abort(); return; }
    fetchInitial(topicId, dataType);
    return () => initAbortRef.current?.abort();
  }, [topicId, dataType, enabled, fetchInitial]);

  const retry = useCallback(
    () => fetchInitial(topicIdRef.current, dataTypeRef.current),
    [fetchInitial],
  );

  return {
    posts, loading, loadingMore, error, hasMore,
    totalCount, dateRange, platformsInfo, loadMore, retry,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// useTopicStream — pure SSE, zero REST calls
// ─────────────────────────────────────────────────────────────────────────────
export function useTopicStream({ topicId, dataType, enabled, topicType, personalized = false }) {
  const [posts,        setPosts]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [totalCount,   setTotalCount]   = useState(0);
  const [connected,    setConnected]    = useState(false);
  const [newLiveCount, setNewLiveCount] = useState(0);

  const esRef          = useRef(null);
  const streamedIdsRef = useRef(new Set());
  const topicTypeRef   = useRef(topicType);
  topicTypeRef.current = topicType;

  const personalizedRef = useRef(personalized);
  personalizedRef.current = personalized;

  const buildUrl = (tid, type, tType, isPersonalized) => {
    const p = new URLSearchParams({ topic_id: tid, stream: "true" });
    if (type !== "all") p.append("data_type", type);
    if (tType) p.append("type", tType);
    if (isPersonalized) p.append("personalized", "true");
    return `${TOPIC_META_SSE_URL}?${p}`;
  };

  const attachHandlers = (es) => {
    es.onopen = () => { setConnected(true); setLoading(false); };

    es.addEventListener("message", (e) => {
      try {
        const post = JSON.parse(e.data);
        setPosts((prev) => [post, ...prev.filter((p) => p._id !== post._id)]);
        streamedIdsRef.current.add(post._id);
        setNewLiveCount((c) => c + 1);
        setTotalCount((c) => c + 1);
      } catch (err) { console.error("SSE parse error:", err); }
    });

    es.addEventListener("end", () => {
      setConnected(false); setLoading(false);
      es.close(); esRef.current = null;
    });

    es.addEventListener("error", (ev) => {
      try { const p = JSON.parse(ev.data); setError(p?.error ?? "Stream error"); }
      catch (_) { /* plain network error */ }
      setConnected(false); setLoading(false);
    });

    es.onerror = () => { setConnected(false); setLoading(false); };
  };

  useEffect(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    if (!enabled || !topicId) { setConnected(false); return; }

    setPosts([]); setError(null); setNewLiveCount(0);
    streamedIdsRef.current = new Set();
    setTotalCount(0); setConnected(false); setLoading(true);

    const es = new EventSource(buildUrl(topicId, dataType, topicType, personalized), { withCredentials: true });
    esRef.current = es;
    attachHandlers(es);

    return () => { es.close(); esRef.current = null; setConnected(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, dataType, enabled, topicType, personalized]);

  const retry = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setConnected(false); setLoading(true); setError(null);

    const es = new EventSource(buildUrl(topicId, dataType, topicTypeRef.current, personalizedRef.current), { withCredentials: true });
    esRef.current = es;
    attachHandlers(es);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, dataType]);

  return {
    posts, loading, error, totalCount, connected, newLiveCount,
    streamedIds: streamedIdsRef.current,
    retry,
  };
}
